import { createContext, useContext, useSyncExternalStore, useCallback } from "react";
import { COLLECTIONS, PERSIST_KEYS, emptyFor, decompose, compose, diffRows, rowSignature, applyRemote } from "./collections.js";

export { PERSIST_KEYS };

/* ───────────── Local (browser-only) storage ─────────────
   New namespace for the rename; anything saved under the Phase 8 key names is read once and carried over. */
export const STORAGE_PREFIX = "ghostwriter:v1:";
const LEGACY_PREFIXES = ["willowvow:v1:"];
export const BACKUP_APP_ID = "ghostwriter-events-dashboard";

export function loadPersisted(key, fallback) {
  try {
    let raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (raw == null) {
      for (const p of LEGACY_PREFIXES) {
        const old = localStorage.getItem(p + key);
        if (old != null) { raw = old; localStorage.setItem(STORAGE_PREFIX + key, old); break; }
      }
    }
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch (e) {
    console.warn("GhostWriter Events: could not read saved data for '" + key + "', using defaults.", e);
    return fallback;
  }
}

export function savePersisted(key, value) {
  try { localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value)); return true; }
  catch (e) { console.warn("GhostWriter Events: could not save '" + key + "'.", e); return false; }
}

export function removePersisted(key) {
  try { localStorage.removeItem(STORAGE_PREFIX + key); } catch (e) { /* ignore */ }
}

// Accepts both the new backup format and Phase 8 ("events-by-ad-dashboard") backups.
export function parseBackup(text) {
  const parsed = JSON.parse(text);
  const state = parsed && parsed.state ? parsed.state : parsed;
  if (!state || typeof state !== "object") throw new Error("Unrecognized backup format.");
  const out = {};
  PERSIST_KEYS.forEach(k => { if (state[k] != null) out[k] = state[k]; });
  if (Object.keys(out).length === 0) throw new Error("No recognizable app data found in this file.");
  return out;
}

function downloadJSON(obj, filename) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

class Emitter {
  constructor() { this.listeners = new Map(); this.statusListeners = new Set(); }
  subscribe(key, cb) {
    if (!this.listeners.has(key)) this.listeners.set(key, new Set());
    this.listeners.get(key).add(cb);
    return () => this.listeners.get(key).delete(cb);
  }
  emit(key) { const s = this.listeners.get(key); if (s) s.forEach(cb => cb()); }
  subscribeStatus(cb) { this.statusListeners.add(cb); return () => this.statusListeners.delete(cb); }
  emitStatus() { this.statusListeners.forEach(cb => cb()); }
}

export class LocalStore extends Emitter {
  constructor() { super(); this.values = new Map(); this.mode = "local"; this.status = { state: "saved" }; }
  get(key, fallback) {
    if (!this.values.has(key)) {
      const v = loadPersisted(key, fallback);
      this.values.set(key, v);
      if (v !== undefined) savePersisted(key, v); // same as Phase 8: defaults are written on first load, so backups include them
    }
    return this.values.get(key);
  }
  set(key, updater) {
    const cur = this.values.get(key);
    const next = typeof updater === "function" ? updater(cur) : updater;
    if (next === cur) return;
    this.values.set(key, next);
    savePersisted(key, next);
    this.emit(key);
  }
  exportBackup() {
    const state = {};
    PERSIST_KEYS.forEach(k => { const v = loadPersisted(k, undefined); if (v !== undefined) state[k] = v; });
    downloadJSON({ app: BACKUP_APP_ID, version: 1, exportedAt: new Date().toISOString(), state }, "ghostwriter-events-backup-" + new Date().toISOString().slice(0, 10) + ".json");
  }
  async importBackup(state) {
    Object.entries(state).forEach(([k, v]) => savePersisted(k, v));
    location.reload();
  }
  resetToSample() {
    PERSIST_KEYS.forEach(k => removePersisted(k));
    location.reload();
  }
}

/* ───────────── Cloud storage (Supabase) ─────────────
   Every collection is held in memory, mirrored to the `records` table row-by-row, and kept live with
   realtime updates so two signed-in planners see each other's changes. */
const SAVE_DELAY_MS = 600;

export class CloudStore extends Emitter {
  constructor(supabase, { studioId, userId, role, displayName, email }) {
    super();
    this.mode = "cloud";
    this.sb = supabase;
    this.studioId = studioId;
    this.userId = userId;
    this.role = role;
    this.displayName = displayName || email || "";
    this.email = email || "";
    this.origin = Math.random().toString(36).slice(2) + Date.now().toString(36);
    this.values = new Map();
    this.synced = new Map(); // key → Map(recKey → signature)
    this.timers = new Map();
    this.inflight = new Map();
    this.dirty = new Set();
    this.status = { state: "saved" };
    this.portalLinks = {}; // clientId → { token, url }
    this.channel = null;
  }

  get isOwner() { return this.role === "owner"; }

  async load() {
    const rows = [];
    const pageSize = 1000;
    for (let from = 0; ; from += pageSize) {
      const { data, error } = await this.sb.from("records").select("collection, rec_key, data, position").eq("studio_id", this.studioId).is("deleted_at", null).range(from, from + pageSize - 1);
      if (error) throw error;
      rows.push(...data);
      if (data.length < pageSize) break;
    }
    const by = {};
    rows.forEach(r => { (by[r.collection] = by[r.collection] || []).push({ recKey: r.rec_key, data: r.data, position: r.position }); });
    Object.keys(COLLECTIONS).forEach(key => {
      const list = by[key] || [];
      this.values.set(key, list.length ? compose(key, list) : emptyFor(key));
      this.synced.set(key, new Map(list.map(r => [r.recKey, rowSignature(r)])));
    });
    await this.loadPortalLinks();
    this.subscribeRealtime();
  }

  async loadPortalLinks() {
    const { data } = await this.sb.from("portal_links").select("client_id, token").eq("studio_id", this.studioId).is("revoked_at", null).order("created_at", { ascending: true });
    const links = {};
    (data || []).forEach(r => { links[r.client_id] = { token: r.token, url: location.origin + "/portal/" + r.token }; });
    this.portalLinks = links;
    this.emit("__portalLinks");
  }

  subscribeRealtime() {
    this.channel = this.sb.channel("records-" + this.studioId)
      .on("postgres_changes", { event: "*", schema: "public", table: "records", filter: "studio_id=eq." + this.studioId }, payload => this.onRemote(payload))
      .subscribe();
  }

  // Deletes arrive as UPDATEs that set deleted_at (gap #11: tombstones), so they pass the access rules.
  // Hard DELETE notices only come from the nightly cleanup of month-old tombstones, which publishes none.
  onRemote(payload) {
    if (payload.eventType === "DELETE") return;
    const row = payload.new;
    if (!row || !COLLECTIONS[row.collection]) return;
    if (row.origin === this.origin) return; // our own write echoing back
    const key = row.collection;
    const sigs = this.synced.get(key) || new Map();
    if (row.deleted_at) {
      if (!sigs.has(row.rec_key)) return;
      sigs.delete(row.rec_key);
      this.values.set(key, applyRemote(key, this.values.get(key), row.rec_key, null, null, true));
    } else {
      const sig = rowSignature({ position: row.position, data: row.data });
      if (sigs.get(row.rec_key) === sig) return;
      sigs.set(row.rec_key, sig);
      this.values.set(key, applyRemote(key, this.values.get(key), row.rec_key, row.data, row.position, false));
    }
    this.synced.set(key, sigs);
    this.emit(key);
  }

  get(key, fallback) {
    if (!COLLECTIONS[key]) return fallback;
    const v = this.values.get(key);
    if (key === "settings") {
      // Settings merge stored values over the defaults so new fields always exist.
      if (!this._settingsCache || this._settingsCache.src !== v) this._settingsCache = { src: v, val: { ...(fallback || {}), ...(v || {}) } };
      return this._settingsCache.val;
    }
    return v === undefined ? emptyFor(key) : v;
  }

  set(key, updater) {
    if (!COLLECTIONS[key]) return;
    const cur = this.get(key);
    const next = typeof updater === "function" ? updater(cur) : updater;
    if (next === cur) return;
    this.values.set(key, key === "settings" ? { ...next, apiKey: undefined } : next);
    this.emit(key);
    this.schedule(key);
  }

  schedule(key, delay) {
    this.dirty.add(key);
    this.setStatus({ state: "saving" });
    clearTimeout(this.timers.get(key));
    this.timers.set(key, setTimeout(() => this.flush(key), delay == null ? SAVE_DELAY_MS : delay));
  }

  async flush(key) {
    clearTimeout(this.timers.get(key));
    if (this.inflight.get(key)) { await this.inflight.get(key); if (this.dirty.has(key)) return this.flush(key); return; }
    if (!this.dirty.has(key)) return;
    this.dirty.delete(key);
    const p = this._write(key).catch(err => {
      console.error("GhostWriter Events: save failed for " + key, err);
      this.dirty.add(key);
      this.setStatus({ state: "error", message: (err && err.message) || "Couldn't save — retrying." });
      clearTimeout(this.timers.get(key));
      this.timers.set(key, setTimeout(() => this.flush(key), 5000));
    }).finally(() => {
      this.inflight.delete(key);
      if (this.dirty.size === 0 && this.status.state !== "error") this.setStatus({ state: "saved", at: Date.now() });
    });
    this.inflight.set(key, p);
    return p;
  }

  async _write(key) {
    let value = this.values.get(key);
    if (key === "settings" && value) { const { apiKey, ...rest } = value; value = rest; }
    const rows = decompose(key, value);
    const sigs = this.synced.get(key) || new Map();
    const { upserts, deletes } = diffRows(rows, sigs);
    if (upserts.length) {
      const payload = upserts.map(r => ({ studio_id: this.studioId, collection: key, rec_key: r.recKey, client_id: r.clientId, data: r.data, position: r.position, updated_by: this.userId, origin: this.origin, deleted_at: null }));
      for (let i = 0; i < payload.length; i += 500) {
        const { error } = await this.sb.from("records").upsert(payload.slice(i, i + 500), { onConflict: "studio_id,collection,rec_key" });
        if (error) throw error;
      }
    }
    if (deletes.length) {
      const { error } = await this.sb.from("records").update({ deleted_at: new Date().toISOString(), updated_by: this.userId, origin: this.origin }).eq("studio_id", this.studioId).eq("collection", key).in("rec_key", deletes);
      if (error) throw error;
    }
    upserts.forEach(r => sigs.set(r.recKey, rowSignature(r)));
    deletes.forEach(k => sigs.delete(k));
    this.synced.set(key, sigs);
    if (this.status.state === "error") this.setStatus({ state: "saving" });
  }

  async flushAll() { await Promise.all(Object.keys(COLLECTIONS).map(k => this.flush(k))); }

  setStatus(s) { this.status = s; this.emitStatus(); }

  exportBackup() {
    const state = {};
    PERSIST_KEYS.forEach(k => { state[k] = this.get(k); });
    downloadJSON({ app: BACKUP_APP_ID, version: 1, exportedAt: new Date().toISOString(), state }, "ghostwriter-events-backup-" + new Date().toISOString().slice(0, 10) + ".json");
  }

  async importBackup(state) {
    Object.entries(state).forEach(([k, v]) => { if (COLLECTIONS[k]) { this.values.set(k, v); this.dirty.add(k); this.emit(k); } });
    this.setStatus({ state: "saving" });
    await this.flushAll();
  }

  actor() { return this.displayName || this.email || "Planner"; }

  // Files live in the private "documents" bucket under <studio>/<client>/…
  async uploadDocFile(clientId, file) {
    const safe = file.name.replace(/[^\w.\- ]+/g, "_").slice(-120);
    const path = this.studioId + "/" + clientId + "/" + Date.now().toString(36) + "-" + safe;
    const { error } = await this.sb.storage.from("documents").upload(path, file, { contentType: file.type || "application/octet-stream", upsert: false });
    if (error) throw error;
    return { path, name: file.name, size: file.size, type: file.type || "" };
  }

  async docFileUrl(path) {
    const { data, error } = await this.sb.storage.from("documents").createSignedUrl(path, 60 * 10);
    if (error) throw error;
    return data.signedUrl;
  }

  async deleteDocFile(path) {
    try { await this.sb.storage.from("documents").remove([path]); } catch (e) { console.warn("File cleanup failed", e); }
  }

  // Turns off a couple's portal link (Remove couple uses it too).
  async revokePortalLink(clientId) {
    const { error } = await this.sb.from("portal_links").update({ revoked_at: new Date().toISOString() }).eq("studio_id", this.studioId).eq("client_id", clientId).is("revoked_at", null);
    await this.loadPortalLinks();
    if (error) throw error;
  }

  async signOut(scope) {
    await this.flushAll().catch(() => {});
    await this.sb.auth.signOut(scope ? { scope } : undefined);
    location.href = "/";
  }

  destroy() { if (this.channel) this.sb.removeChannel(this.channel); this.timers.forEach(t => clearTimeout(t)); }
}

export const localStore = new LocalStore();
const StoreContext = createContext(localStore);
export const StoreProvider = StoreContext.Provider;

let activeStore = localStore;
export function setActiveStore(s) { activeStore = s || localStore; }
export function getActiveStore() { return activeStore; }

export function useStore() { return useContext(StoreContext); }

// Drop-in replacement for useState that saves the value (browser or cloud, depending on the store in context).
export function usePersistentState(key, fallback) {
  const store = useContext(StoreContext);
  const subscribe = useCallback(cb => store.subscribe(key, cb), [store, key]);
  const value = useSyncExternalStore(subscribe, () => store.get(key, fallback));
  const setValue = useCallback(updater => store.set(key, updater), [store, key]);
  return [value, setValue];
}

export function useSaveStatus() {
  const store = useContext(StoreContext);
  return useSyncExternalStore(cb => store.subscribeStatus(cb), () => store.status);
}

export function usePortalLinks() {
  const store = useContext(StoreContext);
  return useSyncExternalStore(cb => store.subscribe("__portalLinks", cb), () => store.portalLinks || null);
}
